import {
  Box,
  SxProps,
  Typography,
  TypographyVariant,
  useTheme,
} from "@mui/material";
import * as React from "react";
import { Link } from "react-router";

import { useAuth } from "@/providers";
import { ActionRecord, ActionReference } from "@/shared/domain/actions";

import { actionDetailPath, ownerOrgIdIfNotViewers } from "../model";

interface ActionLinkProps {
  action: ActionRecord | ActionReference | null;
  component?: React.ElementType;
  /**
   * The org the link is drawn for, when the caller knows it without waiting on the session.
   * Defaults to the auth context's current org, which is null until that session resolves —
   * a grid that paints rows first would otherwise decide ownership against nothing.
   */
  currentOrgId?: string;
  linkSx?: SxProps;
  variant?: TypographyVariant;
  wrapperSx?: SxProps;
  hideOwner?: boolean;
}

const TEXT_BASE_SX = {
  cursor: "pointer",
  fontVariant: "none",
  textDecoration: "none",
};

export function ActionLink({
  action,
  component = "span",
  currentOrgId,
  linkSx,
  variant = "body2",
  wrapperSx,
  hideOwner,
}: ActionLinkProps) {
  const theme = useTheme();
  const { currentOrganization: org } = useAuth();

  if (!action) {
    return null;
  }

  const viewerOrgId = currentOrgId ?? org?.org_id;
  const otherOwner = ownerOrgIdIfNotViewers(action, viewerOrgId);

  const href = actionDetailPath(action, viewerOrgId);
  const displayName =
    otherOwner !== undefined && !hideOwner
      ? `${otherOwner} / ${action.name}`
      : action.name;

  const wrapperStyle = {
    "& a": {
      textDecorationColor: theme.palette.primary.main,
    },
    "& a:not(:hover)": {
      textDecoration: "none",
    },
    ...wrapperSx,
  };

  const textStyle = linkSx ? { ...TEXT_BASE_SX, ...linkSx } : TEXT_BASE_SX;

  return (
    <Box component="span" color={theme.palette.primary.main} sx={wrapperStyle}>
      <Link to={href} onClick={(e) => e.stopPropagation()}>
        <Typography
          component={component}
          title={component === "abbr" ? action.name : undefined}
          color={theme.palette.primary.main}
          fontSize={theme.typography.body2.fontSize}
          sx={textStyle}
          variant={variant}
        >
          {displayName}
        </Typography>
      </Link>
    </Box>
  );
}
